import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Spinner } from '@klaro7/ui';

import { getUserAbuseReview, reviewFraudFlag } from '../services/fraud-admin.service';

const euroFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

const TIMELINE_LABELS: Record<string, string> = {
  registered: 'Registrazione',
  phone_verified: 'Telefono verificato',
  first_call: 'Prima chiamata',
  bonus_exhausted: 'Bonus consumato',
};

const STATUS_BADGE: Record<string, { label: string; variant: 'warning' | 'error' | 'success' }> = {
  open: { label: 'Da revisionare', variant: 'warning' },
  confirmed: { label: 'Abuso confermato', variant: 'error' },
  dismissed: { label: 'Verificato OK', variant: 'success' },
};

/** Certezza 1-10 → colore semaforico coerente in tutta la card. */
function confidenceColor(c: number): string {
  if (c >= 8) return 'text-red-700';
  if (c >= 6) return 'text-amber-600';
  return 'text-neutral-500';
}

function ConfidenceValue({ value }: { value?: number }) {
  if (typeof value !== 'number') return <span className="text-neutral-300">—</span>;
  return (
    <span className={`font-semibold tabular-nums ${confidenceColor(value)}`}>{value}/10</span>
  );
}

/**
 * Card "Segnalazione abuso" nel dettaglio utente admin: perché è flaggato,
 * gli account collegati del cluster, la timeline degli eventi e le azioni di
 * revisione umana (Verificato OK / Conferma abuso, opzionalmente su tutto il
 * cluster). Solo warning: nessun effetto automatico sull'utente.
 */
export function FraudReviewCard({ userId }: { userId: string }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [applyToCluster, setApplyToCluster] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['fraud', 'user-review', userId],
    queryFn: () => getUserAbuseReview(userId),
    retry: false,
  });

  const review = useMutation({
    mutationFn: (action: 'confirm' | 'dismiss') =>
      reviewFraudFlag(data!.flag!.id, {
        action,
        note: note.trim() || undefined,
        applyToCluster,
      }),
    onSuccess: () => {
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['fraud'] });
      void queryClient.invalidateQueries({ queryKey: ['users', 'admin'] });
    },
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-red-200 bg-white p-5">
        <Spinner size="sm" />
      </div>
    );
  }
  const flag = data?.flag;
  if (!flag) return null;

  const status = STATUS_BADGE[flag.status] ?? STATUS_BADGE.open!;
  const isOpen = flag.status === 'open';
  const otherMembers = flag.members.filter((m) => m.userId !== userId);
  const canConfirm = note.trim().length > 0 && !review.isPending;

  return (
    <div className="rounded-xl border border-red-300 bg-white p-5">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-red-700">
          ⚠ Segnalazione abuso multi-account
        </h3>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>
      <p className="mb-3 text-sm text-neutral-500">
        Gruppo <span className="font-mono text-neutral-700">{flag.clusterKey}</span> · punteggio{' '}
        {flag.score} · rilevato il{' '}
        {new Date(flag.flaggedAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
      </p>

      {/* Certezza che sia la stessa persona: complessiva + di questo account */}
      <div className="mb-4 flex flex-wrap gap-4 rounded-lg bg-neutral-50 px-4 py-3">
        <div>
          <div className="text-xs text-neutral-400">Certezza complessiva del gruppo</div>
          <div className="text-xl">
            <ConfidenceValue value={flag.clusterConfidence} />
          </div>
        </div>
        <div>
          <div className="text-xs text-neutral-400">Certezza per questo account</div>
          <div className="text-xl">
            <ConfidenceValue value={flag.confidence} />
          </div>
        </div>
      </div>

      {/* PERCHÉ */}
      <div className="mb-4">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
          Perché è stato segnalato
        </h4>
        <ul className="space-y-1">
          {flag.reasons.map((r) => (
            <li key={r.code} className="flex items-start gap-2 text-sm text-neutral-700">
              <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
              {r.label}
            </li>
          ))}
        </ul>
      </div>

      {/* ACCOUNT COLLEGATI */}
      {otherMembers.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
            Account collegati ({otherMembers.length})
          </h4>
          <div className="overflow-x-auto rounded-lg border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
                  <th className="px-3 py-2">Nome</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Telefono</th>
                  <th className="px-3 py-2">Registrato</th>
                  <th className="px-3 py-2 text-right">Bonus bruciato</th>
                  <th className="px-3 py-2 text-right">Ricariche</th>
                  <th className="px-3 py-2 text-right">Certezza</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {otherMembers.map((m) => (
                  <tr key={m.userId}>
                    <td className="px-3 py-2">
                      <Link
                        to={`/dashboard/admin/users/${m.userId}`}
                        className="font-medium text-brand-teal hover:underline"
                      >
                        {m.name || 'Utente'}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-neutral-600">{m.email}</td>
                    <td className="px-3 py-2 text-neutral-600">{m.phone ?? '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-neutral-600">
                      {new Date(m.registeredAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-red-700">
                      {m.bonusSpent > 0 ? euroFormatter.format(m.bonusSpent) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-neutral-600">{m.rechargeCount}</td>
                    <td className="px-3 py-2 text-right">
                      <ConfidenceValue value={m.confidence} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TIMELINE */}
      {data.timeline.length > 0 && (
        <div className="mb-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-400">
            Timeline del gruppo
          </h4>
          <ol className="max-h-56 space-y-1 overflow-y-auto text-sm">
            {data.timeline.map((e, i) => (
              <li key={`${e.userId}-${e.kind}-${i}`} className="flex flex-wrap items-baseline gap-x-2 text-neutral-600">
                <span className="whitespace-nowrap font-mono text-xs text-neutral-400">
                  {new Date(e.at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
                <span className="font-medium text-neutral-800">{e.userName}</span>
                <span>
                  {TIMELINE_LABELS[e.kind] ?? e.kind}
                  {e.detail ? ` (${e.detail})` : ''}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* AZIONI */}
      {isOpen ? (
        <div className="border-t border-neutral-100 pt-4">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-neutral-400">
            Nota di revisione {`(obbligatoria per confermare l'abuso)`}
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className="mb-3 w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-brand-teal/50 focus:outline-none"
            placeholder="Es. verificato: madre e figlia conviventi / abuso confermato, 5 account stessa persona"
          />
          <label className="mb-3 flex items-center gap-2 text-sm text-neutral-600">
            <input
              type="checkbox"
              checked={applyToCluster}
              onChange={(e) => setApplyToCluster(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300"
            />
            Applica a tutto il gruppo ({flag.members.length} account)
          </label>
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => review.mutate('dismiss')}
              disabled={review.isPending}
            >
              Verificato OK
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={() => review.mutate('confirm')}
              disabled={!canConfirm}
              loading={review.isPending}
            >
              Conferma abuso
            </Button>
          </div>
          <p className="mt-2 text-right text-xs text-neutral-400">
            Nessuna azione automatica: sospensione e storno bonus restano decisioni manuali
            (Gestione utente qui sotto).
          </p>
        </div>
      ) : (
        flag.reviewNote && (
          <p className="border-t border-neutral-100 pt-3 text-sm text-neutral-500">
            Nota revisione: {flag.reviewNote}
          </p>
        )
      )}
    </div>
  );
}
